import React, { useState, useEffect } from 'react';
import { ArrowUpTrayIcon, EyeIcon, TrashIcon, DocumentIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import { useRoutePath } from '../../../utils/getRoutePath';
import { Link } from 'react-router-dom';

const DocumentsTab = ({ register, errors, watch, setValue, inputClassName, documents, setDocuments,customer }) => {
  const [activeSubTab, setActiveSubTab] = useState('documents');
  const [showJobsFilter, setShowJobsFilter] = useState(true);
  const [showEstimatesFilter, setShowEstimatesFilter] = useState(true);
  const [estimates, setEstimates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const getRoutePath = useRoutePath();
   
  useEffect(() => {
    setEstimates(customer?.estimations || []);
    setJobs(customer?.jobs || []);
  }, [customer])
 
  useEffect(() => {
    setDocuments(watch('documents'))
    setValue('documents',watch('documents'))
  }, [watch,setValue,setDocuments])

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    const newDocuments = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      created_at: new Date().toISOString(),
      file: file
    }));
   
    setDocuments([...documents, ...newDocuments]);
    newDocuments.forEach((doc, index) => {
      const docIndex = documents.length + index;
      setValue(`documents.${docIndex}`, doc); // Update only the new index
    });
  };

  const removeDocument = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to remove this document?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        const newDocuments = documents.filter((doc) => doc.id !== id);
        setDocuments(newDocuments);
        setValue('documents', newDocuments);
  
        const existingRemovedIds = watch('document_removed_ids') || [];
        const updatedRemovedIds = [...new Set([...existingRemovedIds, id])];
        setValue('document_removed_ids', updatedRemovedIds);
   
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Mock data for jobs and estimates
  const mockJobs = jobs;
  const mockEstimates = estimates;
   
  const filteredJobs = showJobsFilter ? mockJobs : [];
  const filteredEstimates = showEstimatesFilter ? mockEstimates : [];
 
  return (
    <div className="space-y-8">
      {/* Document Manager */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Document Manager</h3>
        
        {/* Sub-tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
             type="button"
              onClick={() => setActiveSubTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSubTab === 'documents'
                  ? 'border-[#009efb] text-[#009efb]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
            <button
             type="button"
              onClick={() => setActiveSubTab('pictures')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSubTab === 'pictures'
                  ? 'border-[#009efb] text-[#009efb]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Job & Estimate Pictures
            </button>
          </nav>
        </div>

        {/* Documents Tab Content */}
        {activeSubTab === 'documents' && (
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload New Documents
              </label>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <ArrowUpTrayIcon className="w-8 h-8 mb-2 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG (MAX. 10MB each)</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                </label>
              </div>
            </div>

            {documents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No documents uploaded yet</p>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-white border border-gray-200 rounded-lg space-y-2 sm:space-y-0 sm:space-x-4"
                  >
                    {/* File Info */}
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {doc.name.split('.').pop().toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 break-all">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(doc.size)} • Uploaded {formatDate(doc.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        className="text-[#009efb] hover:text-[#0089db]"
                        onClick={() =>
                          window.open(doc.url ? doc.url : URL.createObjectURL(doc.file), '_blank')
                        }
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-800"
                        onClick={() => removeDocument(doc.id)}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}

              </div>
            )}
          </div>
        )}

        {/* Job & Estimate Pictures Tab Content */}
        {activeSubTab === 'pictures' && (
          <div>
            {/* Filters */}
            <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Stored Pictures Filters</h4>
              <div className="flex space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showJobsFilter}
                    onChange={(e) => setShowJobsFilter(e.target.checked)}
                    className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-900">Jobs</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showEstimatesFilter}
                    onChange={(e) => setShowEstimatesFilter(e.target.checked)}
                    className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-900">Estimates</span>
                </label>
              </div>
            </div>

            {/* Jobs */}
            {filteredJobs.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">Jobs</h4>
                <div className="space-y-3">
                  {filteredJobs.map((job) => (
                    <div key={job.id} className="p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h5 className="font-medium text-gray-900">
                           <Link to={getRoutePath(`/jobs/view/${job.id}`)} target="_blank" className="text-[#009efb] hover:text-[#0089db]  hover:underline">{job?.job_number}</Link>
                          </h5>
                          <p className="text-sm text-gray-600 ">{job.title}</p>
                          <p className="text-xs text-gray-500">Date: {job?.formated_created_at}</p>
                          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3'>
                          {job?.pictures.map((pic) => {
                            const extension = pic.name.split('.').pop()?.toLowerCase();

                            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

                            return (
                              <div key={pic.id} className="border rounded p-2 mt-3">
                                
                                {imageExtensions.includes(extension) ? (
                                 <div className="relative group w-[200px] h-[150px]">
                                  <img
                                    src={pic.url}
                                    alt={pic.name}
                                    className="w-full h-full object-cover rounded"
                                  />
                                
                                  <div className="absolute inset-0 bg-black/50 rounded opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                                    <Link  to={`${pic.url}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-white text-black rounded font-semibold">
                                        <EyeIcon className="w-4 h-4 " />
                                    </Link>
                                  </div>
                               </div>
                                ) : (
                                  <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded">
                                    <DocumentIcon className="w-8 h-8 text-gray-500" />
                                  </div>
                                )}

                                {/* <Link
                                  to={`${pic.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-blue-500 text-xs mt-2"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                  View
                                </Link> */}
                              </div>
                            );
                          })}
                          </div>
                         
                        </div>
                         
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Estimates */}
            {filteredEstimates.length > 0 && (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Estimates</h4>
                <div className="space-y-3">
                  {filteredEstimates.map((estimate) => (
                    <div key={estimate.id} className="p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                         <h5 className="font-medium text-gray-900">
                           <Link to={getRoutePath(`/estimations/view/${estimate.id}`)} target="_blank" className="text-[#009efb] hover:text-[#0089db] hover:underline">{estimate?.estimation_number}</Link>
                          </h5>
                          <p className="text-sm text-gray-600">{estimate.title}</p>
                          <p className="text-xs text-gray-500">Date: {estimate?.formated_created_at}</p>
                          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'>
                          {estimate?.pictures.map((pic) => {
                            const extension = pic.name.split('.').pop()?.toLowerCase();

                            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

                            return (
                              <div key={pic.id} className="border rounded p-2 mt-3">
                                
                                {imageExtensions.includes(extension) ? (
                                 <div className="relative group w-[200px] h-[150px]">
                                 <img
                                   src={pic.url}
                                   alt={pic.name}
                                   className="w-full h-full object-cover rounded"
                                 />
                               
                                 <div className="absolute inset-0 bg-black/50 rounded opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                                   <Link  to={`${pic.url}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-white text-black rounded font-semibold">
                                        <EyeIcon className="w-4 h-4 " />
                                    </Link>
                                 </div>
                               </div>
                                ) : (
                                  <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded">
                                    <DocumentIcon className="w-8 h-8 text-gray-500" />
                                  </div>
                                )}

                                {/* <Link
                                  to={`${pic.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-blue-500 text-xs mt-2"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                  View
                                </Link> */}
                              </div>
                            );
                          })}
                          </div>
                           
                        </div>
                        
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredJobs.length === 0 && filteredEstimates.length === 0 && (
              <p className="text-gray-500 text-center py-4">No pictures found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsTab; 